import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { API } from "@/data/campaign";

const PaymentResult = ({ mode }) => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(mode === "success" ? "checking" : "cancelled");
  const [paidInfo, setPaidInfo] = useState(null);

  useEffect(() => {
    if (mode !== "success" || !sessionId) {
      if (mode === "success") setState("error");
      return;
    }
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaidInfo(data);
          setState("paid");
          clearInterval(timer);
        } else if (attempts >= 10) {
          setState("timeout");
          clearInterval(timer);
        }
      } catch {
        if (attempts >= 10) {
          setState("error");
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [mode, sessionId]);

  return (
    <main data-testid={`payment-${mode}-page`} className="flex min-h-screen items-center justify-center px-5 pt-20">
      <div className="w-full max-w-lg border border-pine-700/15 bg-white/60 p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-pine-700" />
            <h1 className="mt-6 font-serif text-3xl font-medium tracking-tight">Confirming your donation…</h1>
            <p className="mt-3 text-sm text-ink/65">One moment while we verify your payment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-pine-600" data-testid="payment-success-icon" />
            <h1 className="mt-6 font-serif text-3xl font-medium tracking-tight">
              Thank you for fuelling <span className="italic text-terra-500">Ward 11’s campaign.</span>
            </h1>
            {paidInfo?.amount && (
              <p data-testid="payment-confirmed-amount" className="mt-4 inline-block border border-pine-700/20 bg-pine-700 px-6 py-3 font-serif text-2xl font-semibold text-paper">
                {(paidInfo.amount).toLocaleString("en-CA", { style: "currency", currency: "CAD" })} confirmed
              </p>
            )}
            <p className="mt-4 text-sm leading-relaxed text-ink/65">
              Your payment has been verified with Stripe and your contribution is recorded. Every dollar goes straight
              to reaching neighbours before election day.
            </p>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <Loader2 className="mx-auto h-10 w-10 text-ink/40" />
            <h1 className="mt-6 font-serif text-3xl font-medium tracking-tight">Still processing</h1>
            <p className="mt-3 text-sm leading-relaxed text-ink/65">
              Your payment is being confirmed. If you completed checkout, your donation is safe — contact{" "}
              aminvoteward11@gmail.com with any question.
            </p>
          </>
        )}
        {state === "cancelled" && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-ink/40" data-testid="payment-cancel-icon" />
            <h1 className="mt-6 font-serif text-3xl font-medium tracking-tight">No charge was made</h1>
            <p className="mt-3 text-sm leading-relaxed text-ink/65">
              You cancelled before completing payment. The campaign is still here whenever you’re ready.
            </p>
          </>
        )}
        <div className="mt-8 flex justify-center gap-3">
          <Link
            to={state === "cancelled" ? "/donate" : "/"}
            data-testid="payment-result-home-link"
            className="bg-pine-700 px-7 py-3.5 text-sm font-semibold text-paper transition-colors hover:bg-terra-500"
          >
            {state === "cancelled" ? "Try again" : "Back to home"}
          </Link>
        </div>
      </div>
    </main>
  );
};

export default PaymentResult;
